import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ShieldCheck, ShieldAlert, ArrowLeft, CheckCircle2, Lock, Loader2, AlertTriangle, RefreshCcw, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
const SECTION_ORDER = ['Pricing Rules', 'Lead Process', 'Professionalism', 'Product Knowledge', 'Event Photos', 'Customer Action', 'Final Confirmation'];

const fmtRel = (iso) => {
  if (!iso) return '';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  if (diff <= 0) return 'now';
  const m = Math.ceil(diff / 60);
  if (m < 60) return `in ${m} minute${m === 1 ? '' : 's'}`;
  return `in ${Math.ceil(m / 60)} hour(s)`;
};

const GgfCertification = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [started, setStarted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ggf/test/questions');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load GGF certification');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
      </div>
    );
  }

  const { status, last_attempt, cooldown_until, questions, pass_threshold_pct, must_pass_question_ids } = data;
  const isCertified = !!status?.ggf_certified;
  const cooldownActive = cooldown_until && new Date(cooldown_until) > new Date();

  const grouped = {};
  for (const q of questions) (grouped[q.group] = grouped[q.group] || []).push(q);
  const totalQ = questions.length;
  const answered = Object.keys(answers).length;

  const submit = async () => {
    if (answered < totalQ) {
      toast.error(`Please answer all ${totalQ} questions before submitting (${answered}/${totalQ})`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/ggf/test/submit', { answers });
      setResult(res.data);
      if (res.data.passed) {
        toast.success(`Passed with ${res.data.score}%!`);
        await load();
      } else {
        toast.error(res.data.failed_reason || `Failed (${res.data.score}%)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50/40">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="ggf-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            Guardian Garage Floors Certification
          </h1>
        </div>

        {/* Status */}
        {isCertified ? (
          <Card className="mb-5 border-2 border-emerald-300 bg-emerald-50" data-testid="ggf-status-certified">
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
              <div>
                <div className="text-lg font-bold text-emerald-900">You are GGF Certified ✅</div>
                <div className="text-sm text-emerald-800">
                  {status.test_score != null ? `Score: ${status.test_score}% · ` : ''}
                  Source: {status.certified_source === 'admin_manual' ? 'Admin manual verification' : 'Self-assessment'}
                  {status.certified_at ? ` · ${formatDate(status.certified_at)}` : ''}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-5 border-amber-300 bg-amber-50" data-testid="ggf-status-not-certified">
            <CardContent className="p-5 flex items-start gap-3">
              <ShieldAlert className="w-8 h-8 text-amber-600 flex-shrink-0" />
              <div>
                <div className="text-lg font-bold text-amber-900">Not yet certified</div>
                <div className="text-sm text-amber-800 mt-1">
                  You must complete this assessment to be eligible for Guardian Garage Floors gigs.
                  Pass mark <strong>{pass_threshold_pct}%</strong> · Pricing-policy questions
                  ({must_pass_question_ids.map((i) => `#${i}`).join(', ')}) <strong>must be answered correctly</strong>.
                </div>
                {last_attempt && (
                  <div className="text-xs text-amber-700 mt-2">
                    Last attempt: {last_attempt.score}% · {last_attempt.passed ? 'Passed' : 'Failed'}
                    {last_attempt.failed_reason ? ` — ${last_attempt.failed_reason}` : ''}
                  </div>
                )}
                {cooldownActive && (
                  <div className="text-xs text-rose-700 mt-1 inline-flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Retake unlocks {fmtRel(cooldown_until)}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {result && (
          <Alert className={`mb-5 ${result.passed ? 'border-emerald-300 bg-emerald-50' : 'border-rose-300 bg-rose-50'}`} data-testid="ggf-result-alert">
            <AlertDescription>
              <div className="font-semibold">
                {result.passed
                  ? `🎉 Passed — ${result.score}% (${result.correct_count}/${result.total_questions})`
                  : `❌ Failed — ${result.score}% (${result.correct_count}/${result.total_questions})`}
              </div>
              {!result.passed && result.failed_reason && (
                <div className="text-sm mt-1">{result.failed_reason}</div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Test */}
        {!isCertified && !cooldownActive && (
          <>
            {!started ? (
              <Card>
                <CardContent className="p-6 text-center space-y-3">
                  {/* Iter-246d — push talent to Learning Center first. Mel
                      asked for this so pass rates improve and clients
                      receive trained talent, not lucky guessers. */}
                  <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-left">
                    <div className="flex items-start gap-3">
                      <BookOpen className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-emerald-900">Review the training materials first</div>
                        <p className="text-xs text-emerald-900/80 mt-1">
                          The Learning Center walks you through everything on this test in 5 short modules
                          (about Guardian Garage Floors, product knowledge, lead capture, booth conduct,
                          and FAQs / objection handling).
                        </p>
                        <Button
                          size="sm"
                          className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => navigate('/talent/certifications/ggf/learn')}
                          data-testid="ggf-open-learning-btn"
                        >
                          <BookOpen className="w-3.5 h-3.5 mr-1.5" />
                          Open GGF Learning Center
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    {totalQ}-question certification. Read each question carefully before answering.
                    A wrong answer to a pricing-policy question is an automatic fail.
                  </p>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => setStarted(true)}
                    data-testid="ggf-start-test-btn"
                  >
                    Start Test
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {SECTION_ORDER.map((section) => {
                  if (!grouped[section]) return null;
                  return (
                    <Card key={section}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          {section}
                          <Badge className="bg-slate-100 text-slate-700 border-slate-300 text-xs">
                            {grouped[section].length} question{grouped[section].length === 1 ? '' : 's'}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        {grouped[section].map((q) => {
                          const isCritical = q.must_pass;
                          return (
                            <div key={q.id} data-testid={`ggf-q-${q.id}`}>
                              <div className="flex items-start gap-2 mb-2">
                                <span className="text-xs font-mono text-gray-500 mt-0.5">#{q.id}</span>
                                <p className="font-medium text-sm text-gray-900 flex-1">
                                  {q.prompt}
                                  {isCritical && (
                                    <span className="ml-2 inline-block bg-rose-100 text-rose-800 border border-rose-300 px-1.5 py-0.5 rounded text-[10px] font-bold">
                                      CRITICAL
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="space-y-1.5 ml-6">
                                {q.options.map((opt, idx) => (
                                  <label
                                    key={idx}
                                    className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition-colors ${
                                      answers[q.id] === idx ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                    data-testid={`ggf-q-${q.id}-opt-${idx}`}
                                  >
                                    <input
                                      type="radio"
                                      name={`q-${q.id}`}
                                      value={idx}
                                      checked={answers[q.id] === idx}
                                      onChange={() => setAnswers({ ...answers, [q.id]: idx })}
                                      className="mt-0.5"
                                    />
                                    <span className="text-sm text-gray-800">{opt}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })}

                <Card className="sticky bottom-4 shadow-lg border-2 border-cyan-300">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      <strong>{answered}</strong> / {totalQ} answered
                      {answered < totalQ && (
                        <span className="text-amber-600 ml-2 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Answer all questions to submit
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={submit}
                      disabled={submitting || answered < totalQ}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="ggf-submit-btn"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      Submit Test
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {cooldownActive && !isCertified && (
          <Card>
            <CardContent className="p-6 text-center text-sm text-gray-600">
              <Lock className="w-8 h-8 mx-auto text-rose-500 mb-2" />
              You can retake this test {fmtRel(cooldown_until)}.
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={load}>
                  <RefreshCcw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GgfCertification;
